import React, { useState, useMemo } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import PortalTooltip from './PortalTooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';

const parseNum = (str) => {
  if (!str) return 0;
  if (typeof str === 'number') return str;
  const num = parseFloat(String(str).replace(/,/g, '').replace(/\$|B|M|K|%/g, (match) => {
    switch (match) {
      case 'B': return 'e9';
      case 'M': return 'e6';
      case 'K': return 'e3';
      case '%': return '';
      default: return '';
    }
  }));
  return isNaN(num) ? 0 : num;
};

  const CustomTooltip = ({ active, payload, hoveredEntityName, coordinate }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      const yItem = payload.length > 1 ? payload[1] : null;
      const zItem = payload.length > 2 ? payload[2] : null;
      
      const isBase = data.isBaseEntity;
      const isHovered = hoveredEntityName === data.name;
      const shouldHighlight = isBase || isHovered;
  
      return (
        <PortalTooltip active={active} payload={payload} coordinate={coordinate}>
          <p className="mb-1" style={{ color: data.color, fontWeight: shouldHighlight ? 'bold' : 'normal' }}>{data.name}</p>
        <p className="text-slate-600">Volume (X): <span className="font-medium text-slate-800">{data.total_pubs?.toLocaleString()}</span></p>
        {yItem && yItem.dataKey && (
          <p className="text-slate-600">Quality (Y): <span className="font-medium text-slate-800">{data[yItem.dataKey] ?? yItem.value}</span></p>
        )}
        {zItem && zItem.dataKey && (
          <p className="text-slate-600">Scale (Z): <span className="font-medium text-slate-800">{data[zItem.dataKey] ?? zItem.value}</span></p>
        )}
      </PortalTooltip>
    );
  }
  return null;
};

const VolumeQualityScatter = ({ entities, analysisRecords = [], isPeerMode }) => {
  const [hoveredEntityName, setHoveredEntityName] = useState(null);
  const [qualityMetric, setQualityMetric] = useState('median_citations_per_paper');
  const [bubbleMetric, setBubbleMetric] = useState('total_grant_amount');

  // Avoid inline array [entity] in render to prevent Recharts infinite loop
  const scatterDataMap = useMemo(() => {
    return entities.map(e => {
      const record = analysisRecords.find(r => r.id === e.id);
      return [{
        ...e,
        total_pubs: parseNum(record?.total_pubs),
        median_citations_per_paper: parseNum(record?.median_citations_per_paper),
        pct_top10_cited: parseNum(record?.pct_top10_cited),
        pct_cited_papers: parseNum(record?.pct_cited_papers),
        h_index: parseNum(record?.h_index),
        specialization_index: parseNum(record?.specialization_index),
        total_grant_amount: parseNum(record?.total_grant_amount),
        faculty_count: parseNum(record?.faculty_count),
        active_researchers: parseNum(record?.active_researchers)
      }];
    });
  }, [entities, analysisRecords]);

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex flex-col xl:flex-row items-center justify-between gap-3 mb-4">
        <div className="text-center xl:text-left">
          <h3 className="text-lg font-semibold text-gray-800">Volume vs Quality Positioning</h3>
          <p className="text-xs text-slate-500">Compare entities on X (Volume), Y (Quality) and Z (Scale)</p>
        </div>
        <div className="flex flex-wrap items-center gap-4 justify-center">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-medium">Y-Axis (Quality):</span>
            <Select value={qualityMetric} onValueChange={setQualityMetric}>
              <SelectTrigger className="w-[180px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="median_citations_per_paper">Median Citations / Paper</SelectItem>
                <SelectItem value="pct_top10_cited">% in Top 10% Cited</SelectItem>
                <SelectItem value="pct_cited_papers">% Cited Papers</SelectItem>
                <SelectItem value="h_index">H-Index</SelectItem>
                <SelectItem value="specialization_index">Specialization Index</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-medium">Bubble (Scale):</span>
            <Select value={bubbleMetric} onValueChange={setBubbleMetric}>
              <SelectTrigger className="w-[160px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="total_grant_amount">Amount Awarded</SelectItem>
                <SelectItem value="faculty_count">Faculty Count</SelectItem>
                <SelectItem value="active_researchers">Active Researchers</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      <div className="flex-1 w-full min-h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis type="number" dataKey="total_pubs" name="Volume" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b" }} />
            <YAxis type="number" dataKey={qualityMetric} name="Quality" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b" }} />
            <ZAxis type="number" dataKey={bubbleMetric} range={[100, 800]} name="Scale" />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<CustomTooltip hoveredEntityName={hoveredEntityName} />} />
            
            {entities.map((entity, index) => {
              const isHovered = hoveredEntityName === entity.name;
              const isAnyHovered = hoveredEntityName !== null;
              
              let opacity = 1;
              let strokeW = 0;
              let strokeColor = "none";
              
              if (isPeerMode) {
                if (entity.isBaseEntity) {
                  opacity = 1;
                  strokeW = 2;
                  strokeColor = "#000";
                } else {
                  opacity = isHovered ? 1 : 0.25;
                  strokeW = isHovered ? 1.5 : 0;
                  strokeColor = isHovered ? "#000" : "none";
                }
              } else {
                opacity = 1;
                strokeW = 0;
                strokeColor = "none";
              }
              
              return (
              <Scatter 
                key={entity.id}
                name={entity.name} 
                data={scatterDataMap[index]} 
                fill={entity.color}
                fillOpacity={opacity}
                stroke={strokeColor}
                strokeWidth={strokeW}
                onMouseEnter={() => setHoveredEntityName(entity.name)}
                onMouseLeave={() => setHoveredEntityName(null)}
              />
            )})}
            <Legend 
              wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} 
              onMouseEnter={(e) => setHoveredEntityName(e.value)}
              onMouseLeave={() => setHoveredEntityName(null)}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default VolumeQualityScatter;
